import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, LogOut, Mail, FileSpreadsheet, FileText, AlertTriangle, Trash2, Share2 } from "lucide-react";
import { useAuth, API } from "../context/AuthContext";
import { formatEuro, DAYS } from "../lib/menu";
import { ToriModeration } from "./ToriModeration";
import { MenuManagement } from "./MenuManagement";
import { GalleryManagement } from "../components/GalleryManagement";
import { SalesOverview } from "../components/SalesOverview";

const statusStyle = (s) => {
  if (s === "paid") return "bg-[#1B6B2E] text-white";
  if (s === "open") return "bg-[#F4D03F] text-[#0D3B4C]";
  return "bg-[#E8F5E9] text-[#0D3B4C]";
};

const statusLabels = { paid: "Betaald", open: "Open", failed: "Mislukt", canceled: "Geannuleerd", expired: "Verlopen" };

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { logout, authHeader } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState("");
  const [dayFilter, setDayFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tab, setTab] = useState("orders");
  const [availSetToday, setAvailSetToday] = useState(true);
  const [selected, setSelected] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [lastFetched, setLastFetched] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const handleAuthError = useCallback((err) => {
    if (err?.response?.status === 401) {
      logout();
      navigate("/admin/login");
    }
  }, [logout, navigate]);

  const loadSettingsStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/settings`, { headers: authHeader() });
      setAvailSetToday(!!data.availability_set_today);
    } catch (err) {
      handleAuthError(err);
    }
  }, [authHeader, handleAuthError]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/orders`, { headers: authHeader() });
      setOrders(data);
      setLastFetched(new Date());
    } catch (err) {
      handleAuthError(err);
      toast.error("Bestellingen laden mislukt.");
    } finally {
      setLoading(false);
    }
  }, [authHeader, handleAuthError]);

  useEffect(() => { load(); loadSettingsStatus(); }, [load, loadSettingsStatus]);

  // Auto-refresh the order list every 30s (only on the orders tab, when enabled).
  useEffect(() => {
    if (tab !== "orders" || !autoRefresh) return;
    const iv = setInterval(() => { load(); }, 30000);
    return () => clearInterval(iv);
  }, [tab, autoRefresh, load]);

  const doExport = async (format) => {
    try {
      const res = await axios.get(`${API}/admin/orders/export?format=${format}`, {
        headers: authHeader(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `bestellingen.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      handleAuthError(err);
      toast.error("Export mislukt.");
    }
  };

  const sendInvoice = async (orderNumber) => {
    setSending(orderNumber);
    try {
      await axios.post(`${API}/admin/orders/${orderNumber}/send-invoice`, {}, { headers: authHeader() });
      toast.success("Factuur verstuurd.");
      load();
    } catch (err) {
      handleAuthError(err);
      toast.error(err?.response?.data?.detail || "Versturen mislukt.");
    } finally {
      setSending("");
    }
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const requestTori = async (orderNumber) => {
    setSending("tori-" + orderNumber);
    try {
      await axios.post(`${API}/admin/orders/${orderNumber}/request-tori`, {}, { headers: authHeader() });
      toast.success("Tori-verzoek verstuurd naar de klant.");
      load();
    } catch (err) {
      handleAuthError(err);
      toast.error(err?.response?.data?.detail || "Versturen mislukt.");
    } finally {
      setSending("");
    }
  };

  const toggleSelect = (num) =>
    setSelected((prev) => (prev.includes(num) ? prev.filter((n) => n !== num) : [...prev, num]));

  const deleteSelected = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`${selected.length} bestelling(en) definitief verwijderen? Dit kan niet ongedaan worden gemaakt.`)) return;
    setDeleting(true);
    try {
      const { data } = await axios.post(`${API}/admin/orders/delete`, { order_numbers: selected }, { headers: authHeader() });
      toast.success(`${data.deleted} bestelling(en) verwijderd.`);
      setSelected([]);
      load();
    } catch (err) {
      handleAuthError(err);
      toast.error(err?.response?.data?.detail || "Verwijderen mislukt.");
    } finally {
      setDeleting(false);
    }
  };

  const requestToriBulk = async () => {
    if (selected.length === 0) return;
    setDeleting(true);
    try {
      const { data } = await axios.post(`${API}/admin/orders/request-tori-bulk`, { order_numbers: selected }, { headers: authHeader() });
      toast.success(`${data.sent} Tori-verzoek(en) verstuurd${data.skipped ? ` · ${data.skipped} overgeslagen` : ""}${data.failed ? ` · ${data.failed} mislukt` : ""}.`);
      setSelected([]);
      load();
    } catch (err) {
      handleAuthError(err);
      toast.error(err?.response?.data?.detail || "Versturen mislukt.");
    } finally {
      setDeleting(false);
    }
  };

  const statuses = useMemo(
    () => Array.from(new Set(orders.map((o) => o.payment_status).filter(Boolean))),
    [orders]
  );

  const filteredOrders = useMemo(
    () => orders.filter(
      (o) => (dayFilter === "all" || o.pickup_day === dayFilter) &&
             (statusFilter === "all" || o.payment_status === statusFilter)
    ),
    [orders, dayFilter, statusFilter]
  );

  const dishTotals = useMemo(() => {
    const totals = {};
    filteredOrders.forEach((o) => {
      (o.items || []).forEach((i) => {
        if (!totals[i.id]) totals[i.id] = { name: i.name, portions: 0 };
        totals[i.id].portions += i.portions;
      });
    });
    return totals;
  }, [filteredOrders]);

  const dishTotalsList = Object.entries(dishTotals);
  const grandPortions = dishTotalsList.reduce((a, [, v]) => a + v.portions, 0);

  const selectCls = "border-2 border-[#0D3B4C] bg-white px-3 py-2 font-[600] text-[#0D3B4C] focus:border-[#B71C1C] focus:outline-none";

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-[#1B6B2E] text-[#F4D03F]">
        <div className="max-w-[1200px] mx-auto w-full px-6 h-20 flex items-center justify-between">
          <h1 className="text-xl font-[700]">Admin · Bestellingen</h1>
          <button onClick={doLogout} data-testid="admin-logout" className="flex items-center gap-2 font-[700] hover:opacity-80 transition-opacity">
            <LogOut size={18} /> Uitloggen
          </button>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto w-full px-6 py-8">
        <div className="flex gap-2 mb-8 border-b-2 border-[#E8F5E9]">
          <button
            onClick={() => setTab("orders")}
            data-testid="tab-orders"
            className={`px-5 py-3 font-[700] -mb-[2px] border-b-2 transition-colors ${tab === "orders" ? "border-[#B71C1C] text-[#B71C1C]" : "border-transparent text-[#0D3B4C] hover:text-[#1B6B2E]"}`}
          >
            Bestellingen
          </button>
          <button
            onClick={() => setTab("menu")}
            data-testid="tab-menu"
            className={`px-5 py-3 font-[700] -mb-[2px] border-b-2 transition-colors ${tab === "menu" ? "border-[#B71C1C] text-[#B71C1C]" : "border-transparent text-[#0D3B4C] hover:text-[#1B6B2E]"}`}
          >
            Menu Beheer
          </button>
          <button
            onClick={() => setTab("gallery")}
            data-testid="tab-gallery"
            className={`px-5 py-3 font-[700] -mb-[2px] border-b-2 transition-colors ${tab === "gallery" ? "border-[#B71C1C] text-[#B71C1C]" : "border-transparent text-[#0D3B4C] hover:text-[#1B6B2E]"}`}
          >
            Homepage Galerij
          </button>
          <button
            onClick={() => setTab("sales")}
            data-testid="tab-sales"
            className={`px-5 py-3 font-[700] -mb-[2px] border-b-2 transition-colors ${tab === "sales" ? "border-[#B71C1C] text-[#B71C1C]" : "border-transparent text-[#0D3B4C] hover:text-[#1B6B2E]"}`}
          >
            Verkoop
          </button>
          <button
            onClick={() => setTab("tori")}
            data-testid="tab-tori"
            className={`px-5 py-3 font-[700] -mb-[2px] border-b-2 transition-colors ${tab === "tori" ? "border-[#B71C1C] text-[#B71C1C]" : "border-transparent text-[#0D3B4C] hover:text-[#1B6B2E]"}`}
          >
            Tori Moderatie
          </button>
        </div>

        {!availSetToday && (
          <div className="flex items-start gap-3 mb-8 bg-[#FEF9E7] border-2 border-[#F4D03F] p-4" data-testid="availability-reminder">
            <AlertTriangle size={22} className="text-[#B71C1C] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-[700] text-[#0D3B4C]">Stel de beschikbaarheid van vandaag in</p>
              <p className="text-sm text-[#0D3B4C]">
                Je hebt de voorraad voor vandaag nog niet ingesteld. Zonder aanpassing staat elk gerecht op de standaardwaarde.
                Ga naar <button onClick={() => setTab("menu")} data-testid="reminder-goto-menu" className="font-[700] text-[#B71C1C] underline">Menu Beheer</button> om het aantal porties per gerecht in te stellen.
              </p>
            </div>
          </div>
        )}

        {tab === "orders" && (
        <>
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <button onClick={load} data-testid="admin-refresh" className="flex items-center gap-2 bg-[#E8F5E9] text-[#0D3B4C] font-[700] px-4 py-2 hover:bg-[#d4ecd7] transition-colors">
            <RefreshCw size={16} /> Vernieuwen
          </button>
          <button onClick={() => doExport("xlsx")} data-testid="export-xlsx" className="flex items-center gap-2 bg-[#1B6B2E] text-white font-[700] px-4 py-2 hover:bg-[#144F22] transition-colors">
            <FileSpreadsheet size={16} /> Export .xlsx
          </button>
          <button onClick={() => doExport("csv")} data-testid="export-csv" className="flex items-center gap-2 bg-[#1B6B2E] text-white font-[700] px-4 py-2 hover:bg-[#144F22] transition-colors">
            <FileText size={16} /> Export .csv
          </button>
          <span className="ml-auto font-[600] text-[#0D3B4C]">{filteredOrders.length} van {orders.length} bestellingen</span>
          <label className="flex items-center gap-2 font-[600] text-[#0D3B4C] cursor-pointer select-none" data-testid="auto-refresh-toggle">
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} className="w-4 h-4 accent-[#1B6B2E]" />
            {autoRefresh && <span className="w-2.5 h-2.5 rounded-full bg-[#1B6B2E] animate-pulse" />}
            Auto-verversen
          </label>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-4 mb-6 bg-[#FEF9E7] p-4">
          <div className="flex flex-col gap-1">
            <label className="font-[600] text-[#0D3B4C] text-sm">Ophaaldag</label>
            <select value={dayFilter} onChange={(e) => setDayFilter(e.target.value)} data-testid="filter-day" className={selectCls}>
              <option value="all">Alle dagen</option>
              {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="font-[600] text-[#0D3B4C] text-sm">Bestelstatus</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="filter-status" className={selectCls}>
              <option value="all">Alle statussen</option>
              {statuses.map((s) => <option key={s} value={s}>{statusLabels[s] || s}</option>)}
            </select>
          </div>
          {(dayFilter !== "all" || statusFilter !== "all") && (
            <button onClick={() => { setDayFilter("all"); setStatusFilter("all"); }} data-testid="filter-reset" className="font-[600] text-[#B71C1C] underline py-2">
              Filters wissen
            </button>
          )}
        </div>

        {/* Day totals per dish */}
        <div className="border-2 border-[#E8F5E9] p-5 mb-8" data-testid="dish-totals">
          <h2 className="font-[700] text-[#0D3B4C] mb-4">
            Dagtotaal per gerecht
            {dayFilter !== "all" ? ` · ${dayFilter}` : " · alle dagen"}
            {statusFilter !== "all" ? ` · ${statusLabels[statusFilter] || statusFilter}` : ""}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {dishTotalsList.length === 0 ? (
              <p className="font-[400] text-[#0D3B4C] col-span-full">Nog geen porties voor deze filters.</p>
            ) : (
              dishTotalsList.map(([id, v]) => (
                <div key={id} className="bg-[#E8F5E9] p-4 flex flex-col items-center text-center" data-testid={`dish-total-${id}`}>
                  <span className="text-3xl font-[700] text-[#1B6B2E]">{v.portions}</span>
                  <span className="font-[600] text-[#0D3B4C] text-sm mt-1">{v.name}</span>
                  <span className="text-xs text-[#0D3B4C]">porties</span>
                </div>
              ))
            )}
          </div>
          <p className="mt-4 font-[700] text-[#0D3B4C]" data-testid="dish-total-grand">Totaal: {grandPortions} porties</p>
        </div>

        {loading ? (
          <p className="font-[600] text-[#0D3B4C]">Laden...</p>
        ) : filteredOrders.length === 0 ? (
          <p className="font-[600] text-[#0D3B4C]" data-testid="no-orders">Geen bestellingen voor deze filters.</p>
        ) : (
          <div>
            <div className="flex flex-wrap items-center gap-3 mb-3" data-testid="orders-bulk-toolbar">
              <span className="font-[600] text-[#0D3B4C] text-sm">{selected.length} geselecteerd</span>
              <button
                onClick={deleteSelected}
                disabled={selected.length === 0 || deleting}
                data-testid="delete-selected-orders"
                className="flex items-center gap-2 bg-[#B71C1C] text-white font-[700] text-sm px-4 py-2 hover:bg-[#8E1515] transition-colors disabled:opacity-40"
              >
                <Trash2 size={15} /> {deleting ? "Verwijderen..." : "Verwijder geselecteerde"}
              </button>
              <button
                onClick={requestToriBulk}
                disabled={selected.length === 0 || deleting}
                data-testid="request-tori-bulk"
                className="flex items-center gap-2 bg-[#1B6B2E] text-white font-[700] text-sm px-4 py-2 hover:bg-[#144F22] transition-colors disabled:opacity-40"
              >
                <Share2 size={15} /> Vraag Tori aan geselecteerde
              </button>
              {selected.length > 0 && (
                <button onClick={() => setSelected([])} data-testid="clear-selection" className="font-[600] text-[#0D3B4C] underline text-sm">
                  Selectie wissen
                </button>
              )}
            </div>
            <div className="overflow-x-auto border-2 border-[#E8F5E9]">
            <table className="w-full text-sm" data-testid="orders-table">
              <thead>
                <tr className="bg-[#E8F5E9] text-[#0D3B4C] text-left">
                  <th className="p-3 font-[700] w-10">
                    <input
                      type="checkbox"
                      data-testid="select-all-orders"
                      aria-label="Selecteer alles"
                      className="w-4 h-4 accent-[#B71C1C]"
                      checked={filteredOrders.length > 0 && filteredOrders.every((o) => selected.includes(o.order_number))}
                      onChange={(e) => {
                        const nums = filteredOrders.map((o) => o.order_number);
                        setSelected((prev) => e.target.checked
                          ? Array.from(new Set([...prev, ...nums]))
                          : prev.filter((n) => !nums.includes(n)));
                      }}
                    />
                  </th>
                  <th className="p-3 font-[700]">Order</th>
                  <th className="p-3 font-[700]">Datum</th>
                  <th className="p-3 font-[700]">Klant</th>
                  <th className="p-3 font-[700]">Gerechten</th>
                  <th className="p-3 font-[700]">Ophalen</th>
                  <th className="p-3 font-[700]">Totaal</th>
                  <th className="p-3 font-[700]">Betaalstatus</th>
                  <th className="p-3 font-[700]">Factuur</th>
                  <th className="p-3 font-[700]">Notities</th>
                  <th className="p-3 font-[700]">Bestek</th>
                  <th className="p-3 font-[700]">Actie</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((o) => (
                  <tr key={o.order_number} className={`border-t border-[#E8F5E9] align-top ${selected.includes(o.order_number) ? "bg-[#FEF9E7]" : ""}`} data-testid={`order-row-${o.order_number}`}>
                    <td className="p-3">
                      <input
                        type="checkbox"
                        data-testid={`select-order-${o.order_number}`}
                        aria-label={`Selecteer ${o.order_number}`}
                        className="w-4 h-4 accent-[#B71C1C]"
                        checked={selected.includes(o.order_number)}
                        onChange={() => toggleSelect(o.order_number)}
                      />
                    </td>
                    <td className="p-3 font-[600] text-[#0D3B4C] whitespace-nowrap">{o.order_number}</td>
                    <td className="p-3 text-[#0D3B4C] whitespace-nowrap">{String(o.created_at).slice(0, 16).replace("T", " ")}</td>
                    <td className="p-3 text-[#0D3B4C]">
                      <div className="font-[600]">{o.customer?.naam}</div>
                      <div className="text-xs">{o.customer?.email}</div>
                      <div className="text-xs">{o.customer?.telefoon}</div>
                    </td>
                    <td className="p-3 text-[#0D3B4C]">
                      {o.items?.map((i) => (
                        <div key={i.id}>{i.name} × {i.portions}</div>
                      ))}
                      <div className="text-xs mt-1">{o.total_portions} porties</div>
                    </td>
                    <td className="p-3 text-[#0D3B4C] whitespace-nowrap">{o.pickup_day}<br />{o.time_slot}</td>
                    <td className="p-3 font-[700] text-[#B71C1C] whitespace-nowrap">{formatEuro(o.total)}</td>
                    <td className="p-3">
                      <span className={`inline-block px-2 py-1 font-[700] text-xs ${statusStyle(o.payment_status)}`}>{statusLabels[o.payment_status] || o.payment_status}</span>
                    </td>
                    <td className="p-3 text-[#0D3B4C] whitespace-nowrap">{o.invoice_sent ? "✔ verstuurd" : "—"}</td>
                    <td className="p-3 text-[#0D3B4C] max-w-[200px]">{o.customer?.opmerkingen || "—"}</td>
                    <td className="p-3 text-[#0D3B4C] whitespace-nowrap" data-testid={`order-cutlery-${o.order_number}`}>{o.cutlery ? `${o.cutlery} sets` : "—"}</td>
                    <td className="p-3">
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => sendInvoice(o.order_number)}
                          disabled={o.payment_status !== "paid" || sending === o.order_number}
                          data-testid={`send-invoice-${o.order_number}`}
                          className="flex items-center gap-1 bg-[#B71C1C] text-white font-[700] text-xs px-3 py-2 hover:bg-[#8E1515] transition-colors disabled:opacity-40 whitespace-nowrap"
                        >
                          <Mail size={14} /> {sending === o.order_number ? "..." : "Factuur"}
                        </button>
                        <button
                          onClick={() => requestTori(o.order_number)}
                          disabled={sending === "tori-" + o.order_number}
                          data-testid={`request-tori-${o.order_number}`}
                          title="Stuur de klant een verzoek om hun Tori te delen"
                          className="flex items-center gap-1 bg-[#1B6B2E] text-white font-[700] text-xs px-3 py-2 hover:bg-[#144F22] transition-colors disabled:opacity-40 whitespace-nowrap"
                        >
                          <Share2 size={14} /> {sending === "tori-" + o.order_number ? "..." : (o.tori_requested ? "Opnieuw vragen" : "Vraag Tori")}
                        </button>
                        {o.tori_requested && (
                          <span className="text-[10px] text-[#1B6B2E] font-[600]" data-testid={`tori-requested-${o.order_number}`}>✔ Tori gevraagd</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {lastFetched && (
              <p className="mt-3 text-xs font-[600] text-[#0D3B4C]/70" data-testid="orders-last-updated">
                Laatst bijgewerkt: {lastFetched.toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "medium" })}
              </p>
            )}
          </div>
        )}
        </>
        )}
        {tab === "menu" && (
          <MenuManagement
            onAuthError={() => { logout(); navigate("/admin/login"); }}
            onSettingsChange={(s) => setAvailSetToday(!!s.availability_set_today)}
          />
        )}
        {tab === "gallery" && (
          <GalleryManagement onAuthError={() => { logout(); navigate("/admin/login"); }} />
        )}
        {tab === "sales" && (
          <SalesOverview onAuthError={() => { logout(); navigate("/admin/login"); }} />
        )}
        {tab === "tori" && (
          <ToriModeration onAuthError={() => { logout(); navigate("/admin/login"); }} />
        )}
      </div>
    </div>
  );
}
